import {
  ApiSuccessResponse,
  BaseSearchRequest,
  GraphBody,
} from "../../@types";
import { apiSlice } from "../../app/api/apiSlice";
import {
  CancelDispatchRequest,
  CreateDispatchRequest,
  Dispatch,
  DispatchGraphData,
  DispatchGraphRequest,
  DispatchImportBatchDetails,
  DispatchImportBatchSearchResponse,
  DispatchImportResult,
  DispatchImportReverseResult,
  DispatchSearchRequest,
  DispatchSearchResponse,
  UpdateDispatchRequest,
} from "./@types";

export const dispatchApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Search dispatches
    searchDispatches: builder.mutation<
      ApiSuccessResponse<DispatchSearchResponse>,
      DispatchSearchRequest
    >({
      query: (requestBody) => ({
        url: "/v1/dispatch/search",
        method: "POST",
        body: requestBody,
      }),
    }),

    // Get dispatch by ID
    getDispatchById: builder.query<ApiSuccessResponse<Dispatch>, string>({
      query: (id) => ({
        url: `/v1/dispatch/getdispatch/${id}`,
        method: "GET",
      }),
    }),

    // Get history dispatches
    getHistoryDispatches: builder.query<
      ApiSuccessResponse<DispatchSearchResponse>,
      void
    >({
      query: () => ({
        url: "/v1/dispatch/dispatch/history",
        method: "GET",
      }),
    }),

    // Get pending dispatches
    getPendingDispatch: builder.query<ApiSuccessResponse<Dispatch>, string>({
      query: (vehicleId) => ({
        url: `/v1/dispatch/pending?vehicleId=${vehicleId}`,
        method: "GET",
      }),
    }),

    // Create dispatch
    createDispatch: builder.mutation<
      ApiSuccessResponse<any>,
      CreateDispatchRequest
    >({
      query: (dispatchData) => ({
        url: "/v1/dispatch",
        method: "POST",
        body: { ...dispatchData },
      }),
    }),

    // Update dispatch
    updateDispatch: builder.mutation<
      ApiSuccessResponse<any>,
      UpdateDispatchRequest
    >({
      query: (dispatchData) => ({
        url: `/v1/dispatch/update/${dispatchData.id}`,
        method: "PUT",
        body: {
          isCompleted: dispatchData.isCompleted,
          incomes: dispatchData.incomes,
          expenses: dispatchData.expenses,
          vehicleId: dispatchData.vehicleId,
          fuelExpense: dispatchData.fuelExpense,
          fromLocation: dispatchData.fromLocation,
          toLocation: dispatchData.toLocation,
          approximateDistance: dispatchData.approximateDistance,
          crews: dispatchData.crews,
        },
      }),
    }),

    // Get dispatch graph data
    getDispatchGraph: builder.mutation<
      ApiSuccessResponse<any>,
      DispatchGraphRequest
    >({
      query: (graphData) => ({
        url: "/v1/dispatch/graph",
        method: "POST",
        body: { ...graphData },
      }),
    }),

    // Cancel dispatch
    cancelDispatch: builder.mutation<
      ApiSuccessResponse<any>,
      CancelDispatchRequest
    >({
      query: (cancelData) => ({
        url: "/v1/dispatch/cancel",
        method: "POST",
        body: { ...cancelData },
      }),
    }),

    getDispatchGraphData: builder.mutation<
      ApiSuccessResponse<DispatchGraphData>,
      GraphBody
    >({
      query: (data) => ({
        url: "/v1/dispatch/graph",
        method: "POST",
        body: { ...data },
      }),
    }),

    // Import dispatches from a spreadsheet
    importDispatches: builder.mutation<
      ApiSuccessResponse<DispatchImportResult>,
      File
    >({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/v1/dispatch/import",
          method: "POST",
          body: formData,
        };
      },
    }),

    // Reverse an import batch
    reverseDispatchImport: builder.mutation<
      ApiSuccessResponse<DispatchImportReverseResult>,
      string
    >({
      query: (importBatchId) => ({
        url: `/v1/dispatch/import/${importBatchId}/reverse`,
        method: "POST",
      }),
    }),

    // Search import batches
    getDispatchImportBatches: builder.mutation<
      ApiSuccessResponse<DispatchImportBatchSearchResponse>,
      BaseSearchRequest
    >({
      query: (requestBody) => ({
        url: "/v1/dispatch/import/batches/search",
        method: "POST",
        body: requestBody,
      }),
    }),

    // Get import batch details
    getDispatchImportBatchById: builder.query<
      ApiSuccessResponse<DispatchImportBatchDetails>,
      string
    >({
      query: (importBatchId) => ({
        url: `/v1/dispatch/import/batches/${importBatchId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useSearchDispatchesMutation,
  useGetDispatchByIdQuery,
  useGetHistoryDispatchesQuery,
  useGetPendingDispatchQuery,
  useCreateDispatchMutation,
  useUpdateDispatchMutation,
  useGetDispatchGraphMutation,
  useCancelDispatchMutation,
  useGetDispatchGraphDataMutation,
  useImportDispatchesMutation,
  useReverseDispatchImportMutation,
  useGetDispatchImportBatchesMutation,
  useGetDispatchImportBatchByIdQuery,
} = dispatchApiSlice;
